import { defaultInstallmentEnabled } from "../../../constants/paymentConstants";
import React, { useEffect, useRef } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Row,
    Col,
    Badge,
    Collapse
} from "reactstrap";
import {
    CARD_TYPES,
    MAX_INSTALLMENTS,
    QUICK_INSTALLMENTS,
    installmentLabel
} from "../utils/formatters";

const styles = `
  .installment-modal .quick-chip {
    border-radius: var(--bs-border-radius-lg, .5rem); border: 1px solid #d7dce3; background: #fff;
    color: #221C5D; font-size: .72rem; font-weight: 600;
    padding: .22rem .7rem; line-height: 1.4; white-space: nowrap;
    transition: background .15s, border-color .15s, color .15s;
  }
  .installment-modal .quick-chip:hover:not(:disabled) { border-color: #221C5D; background: #eef2f8; color: #221C5D; }
  .installment-modal .quick-chip.is-active:hover:not(:disabled) { background: #16123f; border-color: #16123f; color: #fff; }
  .installment-modal .quick-chip.is-active { background: #221C5D; border-color: #221C5D; color: #fff; }
  .installment-modal .quick-chip:disabled { opacity: .55; cursor: not-allowed; }
  .installment-modal .rate-segment {
    display: inline-flex; border: 1px solid #E56A22; border-radius: var(--bs-border-radius-lg, .5rem); overflow: hidden; background: #fff;
  }
  .installment-modal .rate-segment button {
    border: 0; background: transparent; color: #E56A22; font-size: .72rem; font-weight: 600;
    padding: .22rem .75rem; line-height: 1.4; white-space: nowrap; transition: background .15s, color .15s;
  }
  .installment-modal .rate-segment button + button { border-left: 1px solid #E56A22; }
  .installment-modal .rate-segment button:hover:not(:disabled) { background: #fdf0e8; color: #E56A22; }
  .installment-modal .rate-segment button.is-active:hover:not(:disabled) { background: #c9571a; color: #fff; }
  .installment-modal .rate-segment button.is-active { background: #E56A22; color: #fff; }
  .installment-modal .rate-segment button:disabled { opacity: .5; cursor: not-allowed; }
  .installment-modal .rate-hint { font-size: .68rem; color: #94a3b8; }
  .installment-modal .quick-row {
    display: grid; grid-template-columns: 210px 1fr auto; align-items: center; column-gap: 1rem;
  }
  @media (max-width: 991px) {
    .installment-modal .quick-row { grid-template-columns: 1fr; row-gap: .5rem; }
  }
  .installment-modal .quick-chip.clear-chip { border-color: #f1b5b8; color: #dc3545; }
  .installment-modal .quick-chip.clear-chip:hover:not(:disabled) { border-color: #dc3545; background: #fdecee; }
  .installment-modal .quick-divider { width: 1px; align-self: stretch; background: #d7dce3; margin: 0 .15rem; }

  .installment-modal .quick-bar {
    background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px;
    padding: .75rem 1rem; margin-bottom: 1rem;
  }
  .installment-modal .quick-bar .quick-hint { font-size: .75rem; color: #94a3b8; }

  .installment-modal .brand-head { background: #f1f3f5; cursor: pointer; }
  .installment-modal .rate-cell {
    border: 1px solid #e2e8f0; border-radius: 10px; padding: .75rem; background: #fff;
    transition: border-color .15s, background .15s;
  }
  .installment-modal .rate-cell.is-off { background: #f8f9fa; border-color: #edf0f2; }
`;

/**
 * Taksit grubu oluşturma/düzenleme modalı.
 *
 * Izgara 10 marka × 12 taksit = 120 hücre. Hepsini elle gezmek yerine hızlı işlem
 * çipleri var: çip bir ÜST SINIR seçer — "6 Taksit" tek çekimden 6'ya kadar
 * hepsini açar, üstünü kapatır. Aradan taksit çıkarmak için kutucuklar kullanılır.
 */
export default function InstallmentGroupModal({ isOpen, toggle, hookProps }) {
    const {
        groupData,
        setGroupData,            // Form inputlarını güncellemek için
        handleFetchActivePosRates,
        loading,
        companyId,
        canManage,                // Aktif POS oranı çekme yetkisi
        openCard,                 // Hangi kartın akordeonu açık
        toggleCardAccordion,      // Akordeon aç/kapa
        handleToggleCard,         // "Tümünü Seç/Kaldır" fonksiyonu
        handleEnabledChange,      // Checkbox değişimi
        handleRateChange,         // Input değişimi
        applyQuickInstallment,    // Hızlı işlem: yalnız verilen taksiti aç
        applyRateMode,            // Açık taksitlerde vade farkı: %0 ya da Aktif POS oranı
        getEnabledInstallments,   // Markada açık olan taksitler (çip durumu)
        handleSaveGroup           // Kaydet butonu
    } = hookProps;

    // Aynı modal açılışında StrictMode yüzünden 2 kere fetch etmemek için guard
    const autoFetchedRef = useRef(false);

    useEffect(() => {
        // Modal kapandığında flag'i sıfırla; yeni açılışta tekrar auto-fetch yapabilelim
        if (!isOpen) {
            autoFetchedRef.current = false;
            return;
        }

        // canManage yoksa uç 403 döner; her açılışta hata bildirimi göstermemek
        // için oran çekme adımı hiç denenmez.
        if (isOpen && companyId && canManage && !groupData.id && !autoFetchedRef.current) {
            autoFetchedRef.current = true;
            handleFetchActivePosRates();
        }
    }, [isOpen, companyId, canManage, groupData.id, handleFetchActivePosRates]);

    /**
     * Hızlı işlem çip sırası. `card` verilmezse tüm markalara uygulanır.
     *
     * Çip bir ÜST SINIR seçer: "6 Taksit" tek çekimden 6'ya kadar hepsini açar.
     * Dolu görünme koşulu da bununla birebir aynı — kapsamdaki her markada tam
     * olarak 1..N açık ve N'in üstü kapalı olmalı.
     *
     * Alt satırdaki iki düğme açık taksitlerin vade farkını ayarlar: farksız %0,
     * farklı Aktif POS oranları. Açık taksit (tek çekim hariç) hepsi %0 ise
     * "farksız", en az biri %0'dan farklıysa "farklı" dolu görünür.
     */
    const renderQuickChips = ({ card = null, part, showHint = false }) => {
        const scopes = card ? [card] : CARD_TYPES.filter(defaultInstallmentEnabled);
        const isActive = (inst) =>
            scopes.every((brand) => {
                const enabled = getEnabledInstallments(brand);
                return enabled.length === inst && enabled[enabled.length - 1] === inst;
            });

        const openCounts = scopes.flatMap((brand) => getEnabledInstallments(brand).filter((i) => i > 1));
        const openRates = scopes.flatMap((brand) =>
            getEnabledInstallments(brand)
                .filter((i) => i > 1)
                .map((i) => Number(groupData.rates?.[brand]?.[i]?.value || 0))
        );
        const rateMode = openRates.length === 0 ? null : openRates.every((r) => r === 0) ? "free" : "rated";
        const maxOpen = openCounts.length ? Math.max(...openCounts) : 0;
        const range = maxOpen === 2 ? "2 taksit" : `2–${maxOpen} taksit`;
        const hint = rateMode === null
            ? "Önce taksit seçin"
            : rateMode === "free"
                ? `${range}te müşteri vade farkı ödemez`
                : `${range} Aktif POS oranlarıyla`;

        const renderRateOption = (mode, children, title) => (
            <button
                type="button"
                className={rateMode === mode ? "is-active" : ""}
                onClick={(e) => {
                    e.stopPropagation(); // akordeon başlığında: açılıp kapanmasın
                    applyRateMode(mode, card);
                }}
                disabled={loading || rateMode === null}
                title={title}
            >
                {children}
            </button>
        );

        const allOpen = (card ? [card] : CARD_TYPES).every(
            (brand) => getEnabledInstallments(brand).length === MAX_INSTALLMENTS
        );

        if (part === "rate") {
            return (
                <div className="d-flex align-items-center gap-2 justify-content-end">
                    {showHint && <span className="rate-hint">{hint}</span>}
                    <div className="rate-segment" role="group" aria-label="Vade farkı" title={hint}>
                        {renderRateOption("rated", "Vade farklı", "Açık taksitlerde vade farkı Aktif POS komisyon oranları olur")}
                        {renderRateOption("free", "Vade farksız", "Açık taksitlerde vade farkı %0 olur")}
                    </div>
                </div>
            );
        }

        const brands = card ? [card] : CARD_TYPES;
        return (
            <div className="d-flex align-items-center gap-2 flex-wrap">
                {QUICK_INSTALLMENTS.map((inst) => (
                    <button
                        key={inst}
                        type="button"
                        className={`quick-chip ${isActive(inst) ? "is-active" : ""}`}
                        onClick={(e) => {
                            e.stopPropagation(); // akordeon başlığında: açılıp kapanmasın
                            applyQuickInstallment(inst, card);
                        }}
                        disabled={loading}
                        title={
                            inst === 1
                                ? "Yalnız tek çekim açık kalsın"
                                : `Tek çekimden ${inst} taksite kadar açık kalsın`
                        }
                    >
                        {installmentLabel(inst)}
                    </button>
                ))}
                <button
                    type="button"
                    className={`quick-chip ${allOpen ? "is-active" : ""}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        brands.forEach((c) => handleToggleCard(c, true));
                    }}
                    disabled={loading}
                    title="Tüm taksitler açık kalsın"
                >
                    Tümünü seç
                </button>
                <button
                    type="button"
                    className="quick-chip clear-chip"
                    onClick={(e) => {
                        e.stopPropagation();
                        brands.forEach((c) => handleToggleCard(c, false));
                    }}
                    disabled={loading}
                    title="Tüm taksitler kapansın"
                >
                    Tümünü kaldır
                </button>
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            size="xl"
            scrollable
            className="installment-modal"
        >
            <style>{styles}</style>
            <ModalHeader toggle={toggle}>
                {groupData.id ? "Taksit Grubu Düzenle" : "Taksit Grubu Oluştur"}
            </ModalHeader>
            <ModalBody style={{ maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" }}>
                <Form>
                    <Row className="g-3">
                        <Col md={5}>
                            <FormGroup className="mb-0">
                                <Label className="fw-semibold small">Grup Adı *</Label>
                                <Input
                                    name="groupName"
                                    value={groupData.groupName}
                                    onChange={(e) =>
                                        setGroupData((prev) => ({ ...prev, groupName: e.target.value }))
                                    }
                                    placeholder="Örn. Bayi Taksit Grubu"
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col md={7}>
                            <FormGroup className="mb-0">
                                <Label className="fw-semibold small">Açıklama</Label>
                                <Input
                                    type="textarea"
                                    name="description"
                                    value={groupData.description}
                                    onChange={(e) =>
                                        setGroupData((prev) => ({ ...prev, description: e.target.value }))
                                    }
                                    rows={1}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <hr />

                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <h5 className="mb-0">Taksit Oranları</h5>
                        {canManage && (
                            <Button
                                size="sm"
                                className="btn-outline-navy btn-rounded"
                                onClick={() => handleFetchActivePosRates()}
                                disabled={loading || !companyId}
                                title="Yalnız komisyon yüzdelerini POS'tan tazeler; açık/kapalı taksit seçiminize dokunmaz."
                            >
                                <i className="bx bx-cloud-download me-1" /> POS komisyon oranlarını getir
                            </Button>
                        )}
                    </div>

                    {/* TÜM MARKALARA HIZLI İŞLEM */}
                    <div className="quick-bar quick-row">
                        <div>
                            <div className="fw-semibold" style={{ fontSize: ".875rem" }}>
                                Hızlı işlem — tüm markalar
                            </div>
                            <div className="quick-hint">
                                Seçilen taksite kadar hepsi açılır, üstü kapanır.
                            </div>
                        </div>
                        {renderQuickChips({ part: "installments" })}
                        {renderQuickChips({ part: "rate", showHint: true })}
                    </div>

                    {/* KART TİPLERİ DÖNGÜSÜ */}
                    {CARD_TYPES.map((card) => {
                        const isAccordionOpen = openCard === card;
                        const enabledList = getEnabledInstallments(card);

                        return (
                            <div key={card} className="mb-3 border rounded">
                                {/* Akordeon Başlığı */}
                                <div
                                    className="brand-head quick-row px-3 py-2"
                                    onClick={() => toggleCardAccordion(card)}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <strong className="text-primary mb-0">{card === "STANDART" ? "Banka Kartı (Standart)" : card}</strong>
                                        {/* Akordeonu açmadan hangi taksitlerin satışta olduğu
                                            görünsün: 10 markayı tek tek açmadan grubun profili
                                            anlaşılıyor. */}
                                        <Badge
                                            pill
                                            className="fw-normal"
                                            style={{
                                                backgroundColor: enabledList.length ? "#221C5D" : "#B0B7C3",
                                            }}
                                            title={
                                                enabledList.length
                                                    ? enabledList.map(installmentLabel).join(", ")
                                                    : "Bu markada satışa açık taksit yok"
                                            }
                                        >
                                            {enabledList.length
                                                ? `${enabledList.length} seçenek açık`
                                                : "Kapalı"}
                                        </Badge>
                                    </div>

                                    {renderQuickChips({ card, part: "installments" })}

                                    <div className="d-flex align-items-center gap-2 justify-content-end">
                                        {renderQuickChips({ card, part: "rate" })}
                                        <i
                                            className={`bx bx-chevron-${isAccordionOpen ? "up" : "down"} fs-5`}
                                            style={{ color: "#6c757d" }}
                                        />
                                    </div>
                                </div>

                                {/* Akordeon İçeriği (Grid Yapısı) */}
                                <Collapse isOpen={isAccordionOpen}>
                                    <div className="px-3 pb-3 pt-2">
                                        <Row className="g-2">
                                            {[...Array(MAX_INSTALLMENTS)].map((_, i) => {
                                                const inst = i + 1; // Taksit sayısı (1-12)
                                                // İlgili hücre verisini al, yoksa varsayılan değerler
                                                const cellData = groupData.rates?.[card]?.[inst];
                                                const isEnabled = cellData ? cellData.enabled !== false : true;
                                                const cellValue = cellData ? cellData.value ?? "" : "";

                                                return (
                                                    <Col key={inst} xs={6} sm={4} md={3} lg={2}>
                                                        <div className={`rate-cell ${isEnabled ? "" : "is-off"}`}>
                                                            {/* Checkbox (Aktif/Pasif) */}
                                                            <div
                                                                className="d-flex align-items-center mb-3"
                                                                onClick={() =>
                                                                    handleEnabledChange(card, inst, !isEnabled)
                                                                }
                                                                style={{ cursor: "pointer", userSelect: "none" }}
                                                            >
                                                                <input
                                                                    className="form-check-input me-2"
                                                                    type="checkbox"
                                                                    checked={isEnabled}
                                                                    readOnly
                                                                    style={{ marginTop: "0", flexShrink: 0 }}
                                                                />
                                                                <label
                                                                    className="form-check-label mb-0 fw-medium"
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        fontSize: "0.875rem",
                                                                        color: isEnabled ? "#495057" : "#6c757d",
                                                                    }}
                                                                >
                                                                    {installmentLabel(inst)}
                                                                </label>
                                                            </div>

                                                            {/* Oran Inputu */}
                                                            <div>
                                                                <Label className="small mb-2 text-muted fw-medium">
                                                                    Komisyon (%)
                                                                </Label>
                                                                <Input
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0"
                                                                    max="100"
                                                                    value={cellValue}
                                                                    onChange={(e) =>
                                                                        handleRateChange(card, inst, e.target.value)
                                                                    }
                                                                    disabled={loading || !isEnabled}
                                                                    size="sm"
                                                                    placeholder="0.00"
                                                                />
                                                            </div>
                                                        </div>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    </div>
                                </Collapse>
                            </div>
                        );
                    })}
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline className="btn-rounded" onClick={toggle}>
                    İptal
                </Button>
                <Button
                    className="btn-navy btn-rounded"
                    onClick={handleSaveGroup}
                    disabled={loading || !groupData.groupName?.trim()}
                >
                    {loading ? "Kaydediliyor..." : "Kaydet"}
                </Button>
            </ModalFooter>
        </Modal>
    );
}
